import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CalendarDays } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

export interface BlogCardProps {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  category: string;
  imageUrl?: string;
  featured?: boolean;
}

const BlogCard: React.FC<BlogCardProps> = ({ slug, title, date, excerpt, category, imageUrl, featured = false }) => {
  if (featured) {
    return (
      <Link to={`/blog/${slug}`} className="group block relative overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500">
         <div className="absolute inset-0">
            {imageUrl ? (
                <img src={imageUrl} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary/80 to-purple-600/80" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-90" />
         </div>
         <div className="relative p-8 md:p-12 h-full flex flex-col justify-end items-start text-white">
            <Badge className="mb-4 bg-white/20 hover:bg-white/30 text-white border-none backdrop-blur-md uppercase tracking-wider text-xs font-bold px-3 py-1">
                Featured • {category}
            </Badge>
            <h3 className="text-3xl md:text-5xl font-bold mb-4 leading-tight group-hover:text-primary-foreground/90 transition-colors">
                {title}
            </h3>
            <p className="text-lg text-gray-200 mb-6 line-clamp-2 max-w-2xl">
                {excerpt}
            </p>
            <div className="flex items-center gap-2 text-sm font-medium text-gray-300">
                 <span className="flex items-center gap-2"><CalendarDays size={16} /> {new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                 <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                 <span className="flex items-center gap-1 group-hover:translate-x-1 transition-transform text-white">Read Article <ArrowRight size={16} /></span>
            </div>
         </div>
      </Link>
    );
  }

  return (
    <Link to={`/blog/${slug}`} className="group block h-full">
      <Card className="h-full border-none shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden bg-card rounded-2xl flex flex-col">
        <div className="aspect-[16/10] overflow-hidden bg-muted relative">
            {imageUrl ? (
                <img src={imageUrl} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            ) : (
                <div className="w-full h-full flex items-center justify-center bg-primary/5 text-primary/20">
                    <ArrowRight size={48} />
                </div>
            )}
            <div className="absolute top-4 left-4">
                <Badge variant="secondary" className="bg-background/90 backdrop-blur text-xs font-bold shadow-sm border-none">
                    {category}
                </Badge>
            </div>
        </div>
        <CardContent className="p-6 flex flex-col flex-grow">
            <div className="text-xs text-muted-foreground mb-3 flex items-center gap-2">
                <CalendarDays size={14} />
                {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
            <h3 className="text-xl font-bold mb-3 leading-snug group-hover:text-primary transition-colors">
                {title}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-3 mb-4 flex-grow leading-relaxed">
                {excerpt}
            </p>
            <div className="text-sm font-semibold text-primary flex items-center gap-1 group-hover:gap-2 transition-all mt-auto">
                Read Now <ArrowRight size={14} />
            </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default BlogCard;
